import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from "../shadcn/ui/button";
import { Input } from "../shadcn/ui/input";
import { Label } from "../shadcn/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../shadcn/ui/select';
import MediaDialog from './MediaDialog';

import { useToast } from '../shadcn/ui/use-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import config from "../config";
import MediaViewDialog from '../Components/Dialog/MediaViewDialog';


const ProductBasicInfo = ({
    initialValues,
    onSaveBasicInfo,
    parentId,
    categoryOptions = [],
    defaultCategoryId = '',
    onOpenCategoryPanel = () => {},
}) => {
    const [basicInfo, setBasicInfo] = useState({
        name: initialValues?.name || '',
        image: initialValues?.media?.file_path || null,
        category_id: initialValues?.category_id
            ? String(initialValues.category_id)
            : defaultCategoryId
                ? String(defaultCategoryId)
                : '',
        visibility: initialValues?.visibility || 'draft',
        media_id: initialValues?.media_id || '',
        blurhash: initialValues?.blurhash || '',
        small_description: initialValues?.small_description || '',
        large_description: initialValues?.large_description || '',
    });

    const [imagePreview, setImagePreview] = useState(initialValues?.media?.file_path || null);
    const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
    const { toast } = useToast();

    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [errors, setErrors] = useState({});
    const [isCategoryPickerOpen, setIsCategoryPickerOpen] = useState(false);
    const [categorySearch, setCategorySearch] = useState('');
    const categoryPickerRef = useRef(null);
    const normalizedCategoryOptions = useMemo(
        () =>
            (categoryOptions || []).map((option) => ({
                id: String(option.id),
                label: option.label || option.name || option.pathNames?.join(' / ') || option.id,
            })),
        [categoryOptions]
    );

    const handleViewMedia = (mediaItem) => {
        setSelectedMedia(mediaItem);
        setIsViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setIsViewDialogOpen(false);
        setSelectedMedia(null);
    };

    const quillRef = useRef(null);

    useEffect(() => {
        if (!isCategoryPickerOpen) {
            return;
        }
        const handleClick = (event) => {
            if (categoryPickerRef.current && !categoryPickerRef.current.contains(event.target)) {
                setIsCategoryPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isCategoryPickerOpen]);

    useEffect(() => {
        if (initialValues?.category_id) {
            return;
        }
        if (defaultCategoryId) {
            setBasicInfo((prev) => ({ ...prev, category_id: String(defaultCategoryId) }));
        }
    }, [defaultCategoryId, initialValues?.category_id]);

    useEffect(() => {
        if (!basicInfo.category_id) {
            return;
        }
        const exists = normalizedCategoryOptions.some((option) => option.id === basicInfo.category_id);
        if (!exists) {
            setBasicInfo((prev) => ({ ...prev, category_id: '' }));
        }
    }, [normalizedCategoryOptions, basicInfo.category_id]);

    const handleBasicInfoChange = (name, value) => {
        // Update the basicInfo state
        setBasicInfo((prev) => ({ ...prev, [name]: value }));

        // Clear the specific field's error if it exists
        if (errors[name]) {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
        }
    };

    const handleMediaSelect = (selectedMedia) => {
        const imageUrl = selectedMedia[0].file_path;
        const blurhash = selectedMedia[0].blurhash;
        setBasicInfo((prev) => ({ ...prev, image: imageUrl, blurhash: blurhash, media_id: selectedMedia[0].id }));
        // setBasicInfo((prev) => ({ ...prev, image: imageUrl }));
        setImagePreview(imageUrl);
        setIsMediaDialogOpen(false);
    };

    const handleBasicInfoSubmit = async (e) => {
        e.preventDefault();

        // Validation
        let validationErrors = {};

        if (!basicInfo.name.trim()) {
            validationErrors.name = 'Το όνομα απαιτείται.';
        }

        if (!basicInfo.category_id) {
            validationErrors.category_id = 'Απαιτείται κατηγορία.';
        }

        if (basicInfo.visibility === 'public' && !basicInfo.image && !imagePreview) {
            validationErrors.image = 'Απαιτείται εικόνα.';
        }

        if (basicInfo.visibility === 'public' && !basicInfo.small_description.trim()) {
            validationErrors.small_description = 'Απαιτείται μικρή περιγραφή.';
        }

        if (basicInfo.visibility === 'public' && !basicInfo.large_description.trim()) {
            validationErrors.large_description = 'Απαιτείται μεγάλη περιγραφή.';
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            console.log('Validation Errors:', validationErrors);
            return;
        }

        // Prepare data for submission
        const data = new FormData();
        data.append('name', basicInfo.name);
        data.append('category_id', basicInfo.category_id);
        data.append('visibility', basicInfo.visibility);
        data.append('blurhash', basicInfo.blurhash);
        data.append('small_description', basicInfo.small_description);
        data.append('large_description', basicInfo.large_description);

        if (basicInfo.media_id) {
            data.append('media_id', basicInfo.media_id);
        }

        if (basicInfo.image) {
            data.append('image_url', basicInfo.image);
        }

        console.log('Data Being Sent:', Object.fromEntries(data.entries()));

        // Call the onSaveBasicInfo function
        try {
            await onSaveBasicInfo(data);
            toast({
                title: 'Success',
                content: 'Οι πληροφορίες αποθηκεύτηκαν με επιτυχία.',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error saving basic info:', error);
            toast({
                title: 'Error',
                content: 'Η αποθήκευση απέτυχε.',
                variant: 'error',
            });
        }
    };

    const selectedCategory = normalizedCategoryOptions.find(option => option.id === basicInfo.category_id);

    const filteredCategoryOptions = useMemo(() => {
        const term = categorySearch.trim().toLowerCase();
        if (!term) return normalizedCategoryOptions;
        return normalizedCategoryOptions.filter((option) =>
            option.label.toLowerCase().includes(term)
        );
    }, [normalizedCategoryOptions, categorySearch]);

    const handleCategorySelect = (categoryId) => {
        handleBasicInfoChange('category_id', categoryId);
        setIsCategoryPickerOpen(false);
        setCategorySearch('');
    };

    return (
        <div className="w-full mb-10">
            <div className="w-full pb-7">
                <div>
                    <div className="grid gap-4 py-4 sm:grid-cols-1 md:grid-cols-2">
                        {/* Name Row */}
                        <div className="flex flex-col">
                            <Label htmlFor="name" className="text-left mb-1">
                                Όνομα
                                <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                                <Input
                                    id="name"
                                    name="name"
                                    value={basicInfo.name}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value.length <= 25) { // Set max characters to 25
                                            handleBasicInfoChange('name', value);
                                        }
                                    }}
                                    className={`h-10 mb-1 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded px-2`}
                                    placeholder="Όνομα (μέγιστο 25 χαρακτήρες)"
                                />
                                <span className="absolute bottom-2 right-2 text-sm text-gray-500">
                                    {basicInfo.name.length}/25
                                </span>
                            </div>
                            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}

                        </div>

                        {/* Category Row */}
                        <div className="w-full flex flex-col">
                            <Label htmlFor="category_id" className="text-left mb-1">
                                Κατηγορία
                                <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative" ref={categoryPickerRef}>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className={`w-full justify-between ${
                                        errors.category_id ? 'border-red-500' : ''
                                    }`}
                                    disabled={!normalizedCategoryOptions.length}
                                    onClick={() =>
                                        setIsCategoryPickerOpen((prev) => !prev)
                                    }
                                >
                                    {selectedCategory ? selectedCategory.label : 'Επιλέξτε μια κατηγορία'}
                                    <span className="text-xs text-gray-500">
                                        {isCategoryPickerOpen ? '▲' : '▼'}
                                    </span>
                                </Button>
                                {isCategoryPickerOpen && (
                                    <div className="absolute z-20 mt-2 w-full rounded-md border border-gray-200 bg-white p-2 shadow-lg">
                                        <Input
                                            value={categorySearch}
                                            onChange={(e) => setCategorySearch(e.target.value)}
                                            placeholder="Αναζήτηση..."
                                            className="mb-2 h-9"
                                        />
                                        <div className="max-h-60 overflow-y-auto">
                                            {filteredCategoryOptions.length ? (
                                                filteredCategoryOptions.map((category) => (
                                                    <button
                                                        type="button"
                                                        key={category.id}
                                                        className={`flex w-full items-center rounded px-2 py-1 text-left text-sm hover:bg-gray-100 ${
                                                            category.id === basicInfo.category_id
                                                                ? 'bg-gray-100 font-semibold'
                                                                : ''
                                                        }`}
                                                        onClick={() => handleCategorySelect(category.id)}
                                                    >
                                                        {category.label}
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="px-2 py-1 text-sm text-gray-500">
                                                    Δεν βρέθηκαν κατηγορίες
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {errors.category_id && (
                                <p className="text-red-500 text-sm mt-1">{errors.category_id}</p>
                            )}
                            <Button
                                variant="link"
                                type="button"
                                className="mt-1 h-auto p-0 text-left text-sm text-blue-600"
                                onClick={handleOpenPanel}
                            >
                                Διαχείριση κατηγοριών
                            </Button>
                        </div>

                        {/* Image Row */}
                        <div className="flex flex-col">
                            <Label htmlFor="image" className="text-left mb-1">
                                Εικόνα
                                <span className="text-red-500">*</span>
                            </Label>

                            {/* Image Preview */}
                            {imagePreview && (
                                <div className="mb-2">
                                    <img
                                        onClick={() => handleViewMedia(initialValues?.media)}
                                        src={`${config.apiUrl}${imagePreview}`}
                                        alt="Preview"
                                        className={`max-h-20 rounded-md cursor-pointer ${errors.image ? 'border-red-500 border-2' : ''}`}
                                    />
                                </div>
                            )}

                            {/* Select or Change Button */}
                            <Button
                                variant="outline"
                                className={`h-10 w-40 ${errors.image ? 'border-red-500' : ''}`}
                                onClick={() => setIsMediaDialogOpen(true)}
                            >
                                {basicInfo.image_url || imagePreview ? 'Αλλαγή εικόνας' : 'Επιλέξτε εικόνα'}
                            </Button>

                            {/* Error Message */}
                            {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
                        </div>


                        {/* Visibility Row */}
                        <div className="flex flex-col">
                            <Label htmlFor="visibility" className="text-left mb-1">
                                Ορατότητα
                            </Label>
                            <Select
                                value={basicInfo.visibility}
                                onValueChange={(value) => handleBasicInfoChange('visibility', value)}
                                className="block w-full"
                            >
                                <SelectTrigger>
                                    <SelectValue>{basicInfo.visibility === 'draft' ? 'Προσχέδιο' : 'Δημόσιο'}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Προσχέδιο</SelectItem>
                                    <SelectItem value="public">Δημόσιο</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>


                        {/* Small Description Row */}
                        <div className="flex flex-col col-span-full mb-10">
                            <Label htmlFor="small_description" className="text-left mb-1">
                                Μικρή Περιγραφή
                                <span className="text-red-500">*</span>
                            </Label>
                            {errors.small_description && (
                                <p className="text-red-500 text-sm">{errors.small_description}</p>
                            )}
                            <div className="relative">
                                <textarea
                                    id="small_description"
                                    value={basicInfo.small_description}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value.length <= 75) {
                                            handleBasicInfoChange('small_description', value);
                                        }
                                    }}
                                    className={`border rounded p-2 w-full h-20 resize-none ${
                                        errors.small_description ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Μικρή Περιγραφή (μέγιστο 75 χαρακτήρες)"
                                />
                                <span className="absolute bottom-2 right-2 text-sm text-gray-500">
                                    {basicInfo.small_description.length}/75
                                </span>
                            </div>
                        </div>

                        {/* Content Row */}
                        <div className="flex flex-col col-span-full mb-10">
                            <Label htmlFor="large_description" className="text-left mb-1">
                                Μεγάλη Περιγραφή
                                <span className="text-red-500">*</span>
                            </Label>
                            {errors.large_description && <p className="text-red-500 text-sm">{errors.large_description}</p>}
                            <ReactQuill
                                ref={quillRef}
                                id="large_description"
                                value={basicInfo.large_description}
                                onChange={(value) => handleBasicInfoChange('large_description', value)}
                                className="h-[200px]"
                            />
                        </div>
                    </div>

                    <Button className="float-right h-10 mt-4" onClick={handleBasicInfoSubmit}>
                        Αποθήκευση
                    </Button>
                </div>
            </div>

            <MediaDialog
                isOpenMediaDialog={isMediaDialogOpen}
                onCloseMediaDialog={() => setIsMediaDialogOpen(false)}
                onSelectMedia={handleMediaSelect}
                maxSelection={1}
            />

            <MediaViewDialog
                isOpen={isViewDialogOpen}
                onClose={handleCloseViewDialog}
                mediaItem={selectedMedia}
            />
        </div>
    );
};

export default ProductBasicInfo;
    const handleOpenPanel = () => {
        if (typeof onOpenCategoryPanel === 'function') {
            onOpenCategoryPanel();
        }
    };
